/**
 * Billing Page - Subscription and Payment Management
 *
 * CRITICAL: Follows Tim-Combo Pattern (MANDATORY)
 * - Uses useStatus() hook for authentication state
 * - Uses useBrand() for brand-aware theming
 * - Single source of truth (StatusProvider)
 * - Error display from state
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Modal, Badge, Spinner, Alert, Progress, Table } from 'flowbite-react';
import {
  HiCreditCard,
  HiDocumentText,
  HiExternalLink,
  HiTrash,
  HiCheck,
  HiExclamation,
  HiArrowUp,
  HiArrowDown,
  HiClock,
} from 'react-icons/hi';
import { useStatus } from '../contexts/StatusProvider';
import { useBrand } from '../contexts/BrandContext';
import {
  Subscription,
  UsageStats,
  PaymentMethod,
  Invoice,
  PricingPlan,
  getSubscription,
  getUsageStats,
  getPaymentMethods,
  getInvoices,
  getPricingPlans,
  createCheckoutSession,
  createPortalSession,
  cancelSubscription,
  formatCurrency,
  formatDate,
  getStatusInfo,
  getUsagePercentage,
  isSuccessResponse,
  PricingTier,
  BillingPeriod,
} from '../services/billingService';

const BillingPage: React.FC = () => {
  // Tim-Combo Pattern: Use StatusProvider for auth state
  const { Auth_Status, Error_Message: statusError, Client_ID } = useStatus();
  const { primaryColor } = useBrand();

  // Data state
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');

  // Modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | null>(null);
  const [cancelImmediately, setCancelImmediately] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Fetch all billing data
  const fetchBillingData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch all billing data in parallel
      const [subscriptionRes, usageRes, methodsRes, invoicesRes, plansRes] = await Promise.all([
        getSubscription().catch(() => null),
        getUsageStats().catch(() => null),
        getPaymentMethods().catch(() => null),
        getInvoices(10).catch(() => null),
        getPricingPlans().catch(() => null),
      ]);

      if (subscriptionRes && isSuccessResponse(subscriptionRes)) {
        setSubscription(subscriptionRes.data.Records[0] || null);
      }

      if (usageRes && isSuccessResponse(usageRes)) {
        setUsageStats(usageRes.data.Records[0] || null);
      }

      if (methodsRes && isSuccessResponse(methodsRes)) {
        setPaymentMethods(methodsRes.data.Records || []);
      }

      if (invoicesRes && isSuccessResponse(invoicesRes)) {
        setInvoices(invoicesRes.data.Records || []);
      }

      if (plansRes && isSuccessResponse(plansRes)) {
        setPricingPlans(plansRes.data.Records || []);
      }
      // Note: If pricing plans fail to load, the empty array will be shown
      // No fallback data - fail loud principle
    } catch (err) {
      console.error('Error fetching billing data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load billing information');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (Auth_Status) {
      fetchBillingData();
    }
  }, [Auth_Status, fetchBillingData]);

  // Handle plan upgrade/downgrade
  const handlePlanChange = async (plan: PricingPlan) => {
    if (!subscription || plan.tier === subscription.tier) return;

    setSelectedPlan(plan);
    setShowUpgradeModal(true);
  };

  const confirmPlanChange = async () => {
    if (!selectedPlan) return;

    setProcessing(true);
    setError(null);

    try {
      const response = await createCheckoutSession(
        selectedPlan.tier,
        billingPeriod,
        `${window.location.origin}/billing?success=true`,
        `${window.location.origin}/billing?canceled=true`
      );

      if (isSuccessResponse(response) && response.data.Records[0]) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.Records[0].checkout_url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(err instanceof Error ? err.message : 'Failed to process upgrade');
      setProcessing(false);
    }
  };

  // Handle opening Stripe Customer Portal
  const openCustomerPortal = async () => {
    setProcessing(true);
    setError(null);

    try {
      const response = await createPortalSession(`${window.location.origin}/billing`);

      if (isSuccessResponse(response) && response.data.Records[0]) {
        window.location.href = response.data.Records[0].url;
      } else {
        throw new Error('Failed to open billing portal');
      }
    } catch (err) {
      console.error('Error opening portal:', err);
      setError(err instanceof Error ? err.message : 'Failed to open billing portal');
      setProcessing(false);
    }
  };

  // Handle subscription cancellation
  const handleCancelSubscription = async () => {
    setProcessing(true);
    setError(null);

    try {
      const response = await cancelSubscription(cancelImmediately);

      if (isSuccessResponse(response)) {
        setShowCancelModal(false);
        await fetchBillingData();
      } else {
        throw new Error('Failed to cancel subscription');
      }
    } catch (err) {
      console.error('Error canceling subscription:', err);
      setError(err instanceof Error ? err.message : 'Failed to cancel subscription');
    } finally {
      setProcessing(false);
    }
  };

  // Get current plan details
  const currentPlan = pricingPlans.find((p) => p.tier === subscription?.tier) || pricingPlans[0];

  // Tim-Combo Pattern: Display errors from state
  const displayError = error || statusError;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Billing & Subscription
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage your subscription, payment methods, and billing history
        </p>
      </div>

      {/* Error Alert */}
      {displayError && (
        <Alert color="failure" className="mb-4">
          {displayError}
        </Alert>
      )}

      {/* Subscription Status Section */}
      <Card>
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Subscription Status
          </h2>
          {subscription && (
            <Badge color={getStatusInfo(subscription.status).color as 'success' | 'warning' | 'failure' | 'info' | 'gray'}>
              {getStatusInfo(subscription.status).label}
            </Badge>
          )}
        </div>

        {subscription ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Current Plan */}
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">Current Plan</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {currentPlan.name}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {subscription.billing_period === 'annual' ? 'Annual' : 'Monthly'} billing
                </p>
              </div>

              {/* Billing Period */}
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">Current Period</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}
                </p>
              </div>

              {/* Next Billing */}
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {subscription.cancel_at_period_end ? 'Cancels On' : 'Next Billing'}
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatDate(subscription.current_period_end)}
                </p>
                {subscription.cancel_at_period_end && (
                  <p className="text-sm text-yellow-600 dark:text-yellow-400 flex items-center gap-1">
                    <HiExclamation className="w-4 h-4" />
                    Scheduled to cancel
                  </p>
                )}
              </div>
            </div>

            {/* Trial info */}
            {subscription.status === 'trialing' && subscription.trial_end && (
              <Alert color="info">
                <HiClock className="w-4 h-4 mr-2" />
                Your trial ends on {formatDate(subscription.trial_end)}. Add a payment method to continue.
              </Alert>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              No active subscription. Choose a plan to get started.
            </p>
            <Button color="blue" onClick={() => setShowUpgradeModal(true)}>
              Choose a Plan
            </Button>
          </div>
        )}
      </Card>

      {/* Usage Stats Section */}
      {usageStats && (
        <Card>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Current Period Usage
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Executions */}
            <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Executions</p>
                <Badge color={getUsagePercentage(usageStats.executions_used, usageStats.executions_limit).color as 'success' | 'warning' | 'failure'}>
                  {getUsagePercentage(usageStats.executions_used, usageStats.executions_limit).percentage}%
                </Badge>
              </div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {usageStats.executions_used.toLocaleString()} / {usageStats.executions_limit.toLocaleString()}
              </p>
              <Progress
                progress={getUsagePercentage(usageStats.executions_used, usageStats.executions_limit).percentage}
                color={getUsagePercentage(usageStats.executions_used, usageStats.executions_limit).color as 'green' | 'yellow' | 'red'}
                size="sm"
                className="mt-2"
              />
            </div>

            {/* Agents */}
            <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Agents</p>
                <Badge color={getUsagePercentage(usageStats.agents_used, usageStats.agents_limit).color as 'success' | 'warning' | 'failure'}>
                  {getUsagePercentage(usageStats.agents_used, usageStats.agents_limit).percentage}%
                </Badge>
              </div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {usageStats.agents_used} / {usageStats.agents_limit}
              </p>
              <Progress
                progress={getUsagePercentage(usageStats.agents_used, usageStats.agents_limit).percentage}
                color={getUsagePercentage(usageStats.agents_used, usageStats.agents_limit).color as 'green' | 'yellow' | 'red'}
                size="sm"
                className="mt-2"
              />
            </div>

            {/* Storage */}
            <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Storage</p>
                <Badge color={getUsagePercentage(usageStats.storage_used_gb, usageStats.storage_limit_gb).color as 'success' | 'warning' | 'failure'}>
                  {getUsagePercentage(usageStats.storage_used_gb, usageStats.storage_limit_gb).percentage}%
                </Badge>
              </div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {usageStats.storage_used_gb.toFixed(1)} GB / {usageStats.storage_limit_gb} GB
              </p>
              <Progress
                progress={getUsagePercentage(usageStats.storage_used_gb, usageStats.storage_limit_gb).percentage}
                color={getUsagePercentage(usageStats.storage_used_gb, usageStats.storage_limit_gb).color as 'green' | 'yellow' | 'red'}
                size="sm"
                className="mt-2"
              />
            </div>

            {/* API Calls */}
            <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">API Calls</p>
                <Badge color={getUsagePercentage(usageStats.api_calls, usageStats.api_calls_limit).color as 'success' | 'warning' | 'failure'}>
                  {getUsagePercentage(usageStats.api_calls, usageStats.api_calls_limit).percentage}%
                </Badge>
              </div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {usageStats.api_calls.toLocaleString()} / {usageStats.api_calls_limit.toLocaleString()}
              </p>
              <Progress
                progress={getUsagePercentage(usageStats.api_calls, usageStats.api_calls_limit).percentage}
                color={getUsagePercentage(usageStats.api_calls, usageStats.api_calls_limit).color as 'green' | 'yellow' | 'red'}
                size="sm"
                className="mt-2"
              />
            </div>
          </div>
        </Card>
      )}

      {/* Plan Selection Section */}
      <Card>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Available Plans
          </h2>
          <div className="flex gap-2">
            <Button
              size="sm"
              color={billingPeriod === 'monthly' ? 'blue' : 'light'}
              onClick={() => setBillingPeriod('monthly')}
            >
              Monthly
            </Button>
            <Button
              size="sm"
              color={billingPeriod === 'annual' ? 'blue' : 'light'}
              onClick={() => setBillingPeriod('annual')}
            >
              Annual (Save 17%)
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {pricingPlans.map((plan) => {
            const isCurrentPlan = subscription?.tier === plan.tier;
            const price = billingPeriod === 'annual' ? plan.annualPrice / 12 : plan.monthlyPrice;
            const isUpgrade = subscription && getTierOrder(plan.tier) > getTierOrder(subscription.tier);
            const isDowngrade = subscription && getTierOrder(plan.tier) < getTierOrder(subscription.tier);

            return (
              <div
                key={plan.tier}
                className={`relative p-4 border-2 rounded-lg ${
                  isCurrentPlan
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : plan.isPopular
                    ? 'border-purple-500'
                    : 'border-gray-200 dark:border-gray-600'
                }`}
              >
                {plan.isPopular && !isCurrentPlan && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-purple-500 text-white text-xs font-medium rounded-full">
                    Most Popular
                  </span>
                )}

                {isCurrentPlan && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-500 text-white text-xs font-medium rounded-full">
                    Current Plan
                  </span>
                )}

                <div className="pt-2">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    {plan.name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    {plan.description}
                  </p>

                  <div className="mb-4">
                    {plan.tier === 'enterprise' ? (
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        Custom
                      </p>
                    ) : plan.tier === 'free' ? (
                      <>
                        <span className="text-3xl font-bold text-gray-900 dark:text-white">
                          Free
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-3xl font-bold text-gray-900 dark:text-white">
                          {formatCurrency(price)}
                        </span>
                        <span className="text-gray-500 dark:text-gray-400">/user/mo</span>
                      </>
                    )}
                  </div>

                  <ul className="space-y-2 mb-4">
                    {plan.features.slice(0, 4).map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                        <HiCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {isCurrentPlan ? (
                    <Button color="light" disabled className="w-full">
                      Current Plan
                    </Button>
                  ) : plan.tier === 'enterprise' ? (
                    <Button color="dark" className="w-full" onClick={() => window.open('mailto:sales@equilateral.ai')}>
                      Contact Sales
                    </Button>
                  ) : (
                    <Button
                      color={isUpgrade ? 'blue' : 'light'}
                      className="w-full"
                      onClick={() => handlePlanChange(plan)}
                    >
                      {isUpgrade ? (
                        <>
                          <HiArrowUp className="w-4 h-4 mr-2" />
                          Upgrade
                        </>
                      ) : isDowngrade ? (
                        <>
                          <HiArrowDown className="w-4 h-4 mr-2" />
                          Downgrade
                        </>
                      ) : (
                        'Select Plan'
                      )}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Payment Methods Section */}
      <Card>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Payment Methods
          </h2>
          <Button color="light" onClick={openCustomerPortal} disabled={processing}>
            <HiCreditCard className="w-4 h-4 mr-2" />
            Manage in Stripe
          </Button>
        </div>

        {paymentMethods.length > 0 ? (
          <div className="space-y-3">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg"
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded">
                    <HiCreditCard className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {method.brand ? method.brand.charAt(0).toUpperCase() + method.brand.slice(1) : 'Card'} ending in {method.last4}
                    </p>
                    {method.exp_month && method.exp_year && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Expires {method.exp_month}/{method.exp_year}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {method.is_default && (
                    <Badge color="success">Default</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <HiCreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              No payment methods on file
            </p>
            <Button color="blue" onClick={openCustomerPortal} disabled={processing}>
              Add Payment Method
            </Button>
          </div>
        )}
      </Card>

      {/* Billing History Section */}
      <Card>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Billing History
          </h2>
          <Button color="light" onClick={openCustomerPortal} disabled={processing}>
            <HiExternalLink className="w-4 h-4 mr-2" />
            View All in Stripe
          </Button>
        </div>

        {invoices.length > 0 ? (
          <div className="overflow-x-auto">
            <Table hoverable>
              <Table.Head>
                <Table.HeadCell>Invoice</Table.HeadCell>
                <Table.HeadCell>Date</Table.HeadCell>
                <Table.HeadCell>Period</Table.HeadCell>
                <Table.HeadCell>Amount</Table.HeadCell>
                <Table.HeadCell>Status</Table.HeadCell>
                <Table.HeadCell>Actions</Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {invoices.map((invoice) => (
                  <Table.Row key={invoice.id} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                    <Table.Cell className="font-medium text-gray-900 dark:text-white">
                      {invoice.invoice_number}
                    </Table.Cell>
                    <Table.Cell>{formatDate(invoice.created_at)}</Table.Cell>
                    <Table.Cell>
                      {formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}
                    </Table.Cell>
                    <Table.Cell>{formatCurrency(invoice.amount_paid || invoice.amount_due, invoice.currency)}</Table.Cell>
                    <Table.Cell>
                      <Badge color={invoice.status === 'paid' ? 'success' : invoice.status === 'open' ? 'warning' : 'gray'}>
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex gap-2">
                        {invoice.invoice_pdf && (
                          <Button
                            size="xs"
                            color="light"
                            onClick={() => window.open(invoice.invoice_pdf!, '_blank')}
                          >
                            <HiDocumentText className="w-4 h-4" />
                          </Button>
                        )}
                        {invoice.hosted_invoice_url && (
                          <Button
                            size="xs"
                            color="light"
                            onClick={() => window.open(invoice.hosted_invoice_url!, '_blank')}
                          >
                            <HiExternalLink className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8">
            <HiDocumentText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              No invoices yet
            </p>
          </div>
        )}
      </Card>

      {/* Cancel Subscription Section */}
      {subscription && subscription.status === 'active' && !subscription.cancel_at_period_end && (
        <Card>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Cancel Subscription
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            If you cancel your subscription, you will lose access to premium features at the end of your current billing period.
          </p>
          <Button color="failure" outline onClick={() => setShowCancelModal(true)}>
            <HiTrash className="w-4 h-4 mr-2" />
            Cancel Subscription
          </Button>
        </Card>
      )}

      {/* Upgrade/Downgrade Confirmation Modal */}
      <Modal show={showUpgradeModal} onClose={() => setShowUpgradeModal(false)}>
        <Modal.Header>
          {selectedPlan && subscription && getTierOrder(selectedPlan.tier) > getTierOrder(subscription.tier)
            ? 'Upgrade Plan'
            : 'Change Plan'}
        </Modal.Header>
        <Modal.Body>
          {selectedPlan && (
            <div className="space-y-4">
              <p className="text-gray-700 dark:text-gray-300">
                You are about to change to the <strong>{selectedPlan.name}</strong> plan.
              </p>

              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-300">New Price</span>
                  <span className="text-lg font-bold text-gray-900 dark:text-white">
                    {formatCurrency(billingPeriod === 'annual' ? selectedPlan.annualPrice / 12 : selectedPlan.monthlyPrice)}/mo
                  </span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-gray-600 dark:text-gray-300">Billing</span>
                  <span className="text-gray-900 dark:text-white">
                    {billingPeriod === 'annual' ? 'Annually' : 'Monthly'}
                  </span>
                </div>
              </div>

              <p className="text-sm text-gray-500 dark:text-gray-400">
                You will be redirected to Stripe to complete the payment.
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={confirmPlanChange} disabled={processing}>
            {processing ? <Spinner size="sm" className="mr-2" /> : null}
            Continue to Payment
          </Button>
          <Button color="gray" onClick={() => setShowUpgradeModal(false)} disabled={processing}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancel Confirmation Modal */}
      <Modal show={showCancelModal} onClose={() => setShowCancelModal(false)} size="md">
        <Modal.Header>Cancel Subscription</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              Are you sure you want to cancel your subscription?
            </p>

            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                <input
                  type="radio"
                  name="cancelOption"
                  checked={!cancelImmediately}
                  onChange={() => setCancelImmediately(false)}
                  className="w-4 h-4 text-blue-600"
                />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    Cancel at period end
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Keep access until {subscription && formatDate(subscription.current_period_end)}
                  </p>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                <input
                  type="radio"
                  name="cancelOption"
                  checked={cancelImmediately}
                  onChange={() => setCancelImmediately(true)}
                  className="w-4 h-4 text-blue-600"
                />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    Cancel immediately
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Lose access right away (no refund)
                  </p>
                </div>
              </label>
            </div>

            <Alert color="warning">
              <HiExclamation className="w-4 h-4" />
              <span className="ml-2">
                All your data will be retained for 30 days after cancellation.
              </span>
            </Alert>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button color="failure" onClick={handleCancelSubscription} disabled={processing}>
            {processing ? <Spinner size="sm" className="mr-2" /> : null}
            Confirm Cancellation
          </Button>
          <Button color="gray" onClick={() => setShowCancelModal(false)} disabled={processing}>
            Keep Subscription
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// Helper function to determine tier order for upgrade/downgrade logic
function getTierOrder(tier: PricingTier): number {
  const order: Record<PricingTier, number> = {
    free: 0,
    starter: 1,
    professional: 2,
    enterprise: 3,
  };
  return order[tier] ?? 0;
}

export default BillingPage;
